import { CouponData } from '@app/models/CouponData';
import { PaymentSchedule } from '@app/models/PaymentSchedule';
import { Property } from '@app/models/Property';
import { NetworkErrorService } from '@app/services/NetworkError.service';
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { HomeownerApi } from '@app/services/apiServices/HomeownerApi';
import { PaymentScheduleApi } from '@app/services/apiServices/PaymentScheduleApi';
import { PropertyApi } from '@app/services/apiServices/PropertyApi';
import { SpinnerService } from '@app/services/spinner.service';
import { UserStore } from '@app/services/stores/UserStore';
import { Address } from '@app/models/Address';
import { ContactApi } from '@app/services/apiServices/ContactApi';
import { take } from "rxjs/operators";
import { Component, Inject } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { forkJoin, Subject } from 'rxjs';
import { RegistrationNoPropertyDialog } from "@app/pages/registration/registrationDialogNoProperty.component";
import { OneButtonDialogComponent } from "@app/components/sharedDialogs/oneButtonDialog.component";
import { DialogErrorDialog } from "@app/components/sharedDialogs/dialogError.dialog";
import { RegistrationStopHoldDialog } from "@app/pages/registration/registrationDialogStopHold.component";
import { PropertydetailsMovedDialog } from "@app/pages/properties/propertydetails/propertydetailsMovedDialog.component";
import { RegistrationInvalidAddressDialog } from "@app/pages/registration/registrationDialogInvalidAddress.component";
import { RegistrationAltAddressNeedsCorrectionDialog } from "@app/pages/registration/registrationDialogAltAddressNeedsCorrection.component";
import { RegistrationAltAddressDialog } from "@app/pages/registration/registrationDialogAltAddress.component";
import { PropertydetailsRemoveDialog } from "@app/pages/properties/propertydetails/propertydetailsRemoveDialog.component";
import { AppDataService } from "@app/services/appData.service";
import { ActivatedRoute, Router } from "@angular/router";

/**
 * Created by mac85103 on 9/7/16.
 */
@Component({
    selector: 'app-property-details',
    template: require('./propertydetails.html')
})
export class PropertyDetailsComponent {

    propertyId: number;
    property: Property = { address: { }, hoa: { pmc: { } } };
    paymentSchedules: PaymentSchedule[];
    staticPropertyNickname: string;
    disableInputsAddress: boolean = true;
    disablePropertyId: boolean = false;
    disableInputsProperty: boolean = false;
    count: number = 0;
    doneLoading: boolean;
    isLoadingFindMyProperty: boolean = false;
    invalidCheckVar: boolean = false;
    propertyNicknameError: boolean = false;
    nickname: string;
    nicknameChanged: boolean = false;
    isCorrectingAddress: boolean = false;
    setPropertyAddressSubject: Subject<void> = new Subject<void>();
    getWebUrl: () => string;
    getPmcContactUrl = (pmcId: string) => this.getWebUrl() + `/guest/#/contact/?pmcId=${pmcId}`;

    constructor(@Inject(MatDialog) private dialog: MatDialog,
                @Inject(PropertyApi) private propertyApi: PropertyApi,
                @Inject(ContactApi) private ContactApi: ContactApi,
                @Inject(HomeownerApi) private homeownerApi: HomeownerApi,
                @Inject(NetworkErrorService) private NetworkErrorService: NetworkErrorService,
                @Inject(ErrorHandlerService) private ErrorHandlerService: ErrorHandlerService,
                @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
                @Inject(SpinnerService) private SpinnerService: SpinnerService,
                @Inject(UserStore) private userStore: UserStore,
                @Inject(AppDataService) private appDataService: AppDataService,
                @Inject(Router) private router: Router,
                @Inject(ActivatedRoute) private route: ActivatedRoute) {

        this.getWebUrl = () => appDataService.appConfig.get_APP_ENV().HOAPASS_WEB_URL;

        if (this.route.snapshot.params.propertyId) {
            SpinnerService.showSpinner();
            this.propertyId = this.route.snapshot.params.propertyId;
            this.doneLoading = false;
            this.staticPropertyNickname = "";
            this.getPropertyDetails().then(() => {
                SpinnerService.closeSpinner();
                this.getIsAddressEnabled();
                this.doneLoading = true;
            }, () => {
                this.doneLoading = true;
            });
        }
    }

    getIsAddressEnabled() {
        if (this.isCouponDataPresent()) {
            this.propertyApi.propertyAddressGet(false,
                this.property.hoa.pmc.couponId,
                this.property.hoa.couponId,
                this.property.couponId)
                .pipe(take(1)).subscribe((resp) => {
                    this.disableInputsAddress = !!(resp.addressLine1 || resp.line1);
                });
        }
    }

    getPropertyDetails(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.propertyApi.propertiesIdGet(this.propertyId)
                .pipe(take(1)).subscribe((resp) => {
                    this.property = resp;
                    this.nickname = this.property.nickname;
                    this.staticPropertyNickname = this.property.nickname;

                    this.paymentScheduleApi.propertiesIdPaymentSchedulesGet(this.propertyId)
                        .pipe(take(1)).subscribe((resp) => {
                            this.paymentSchedules = resp;
                            resolve();
                        }, (err) => {
                            console.error('propertiesIdPaymentSchedulesGet', err);
                            this.NetworkErrorService.handleNetworkError(err, true)
                                .pipe(take(1)).subscribe((resp) => {
                                    if (resp === true) {
                                        this.getPropertyDetails();
                                    }
                                    reject();
                                });
                        });

                },(err) => {
                    console.error("Properties ID Get error: ", err);
                    this.NetworkErrorService.handleNetworkError(err, true).pipe(take(1)).subscribe((resp) => {
                        if (resp === true) {
                            this.getPropertyDetails();
                        }
                        reject();
                    });
                });
        });
    }

    isAddressPresent() {
        return (this.property && this.property.address
            && this.property.address.line1 && this.property.address.line1.toString().length > 0
            && this.property.address.city && this.property.address.city.toString().length > 0
            && this.property.address.state && this.property.address.state.toString().length > 0
            && this.property.address.postalCode && this.property.address.postalCode.toString().length > 0
            && this.nickname && this.nickname.toString().length > 0);
    }

    isAddressFieldValid(value) {
        return value && value.length > 0 && value.toLowerCase() !== 'null'
    }

    isAddressValid(address: Address) {
        return (this.isAddressFieldValid(address.line1) || this.isAddressFieldValid(address.addressLine1))
            && this.isAddressFieldValid(address.city)
            && this.isAddressFieldValid(address.state)
            && this.isAddressFieldValid(address.postalCode)
            && new RegExp('^[0-9]{5}$').test(address.postalCode);
    }

    disableAddressInputs() {
        if (!this.isCorrectingAddress) {
            this.disableInputsAddress = true;
        }
    }

    startCorrectingAddress() {
        this.isCorrectingAddress = true;
        this.disableInputsAddress = false;
    }

    stopCorrectingAddress() {
        this.isCorrectingAddress = false;
        this.disableInputsAddress = true;
    }

    cleanAddress(address: Address) {
        if (address.postalCode !== null) {
            address.postalCode = address.postalCode.substr(0, 5);
        }
    }

    setPropertyAddress(address: Address) {
        this.cleanAddress(address);
        if (!this.isAddressValid(address)) {
            this.startCorrectingAddress();
        } else {
            this.stopCorrectingAddress();
        }

        this.property.address = address;
        this.setPropertyAddressSubject.next();
    }

    updateDefaultPropertyAddress() {
        this.stopCorrectingAddress();
        this.defaultPropertyAddress();
    }

    removeProperty() {
        this.SpinnerService.showSpinner();
        let hasPaymentSchedules = false;
        if (this.paymentSchedules != null) {
            hasPaymentSchedules = this.paymentSchedules && this.paymentSchedules.length > 0;
        }

        let dialogRef = this.dialog.open(PropertydetailsRemoveDialog, {
            closeOnNavigation: true,
            disableClose: true,
            data: { hasPaymentSchedules: hasPaymentSchedules, paymentSchedules: this.paymentSchedules }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe((data) => {
            if (data === 'Remove') {
                let observables = [];
                for (let i = 0; i < this.paymentSchedules.length; i++) {
                    observables.push(this.paymentScheduleApi.paymentSchedulesIdDelete(this.paymentSchedules[i].id).pipe(take(1)));
                }

                forkJoin(observables).subscribe(() => {
                    //handling in complete block since this is skipped when there are no observables
                }, (err) => {
                    let errMsg: string = undefined;
                    if (err.error && err.error.message) {
                        errMsg = err.error.message;
                    }
                    this.ErrorHandlerService.handleNetworkError(err, true, errMsg).pipe(take(1)).subscribe();
                }, () => {
                    this.propertyApi.propertiesIdDelete(this.propertyId).pipe(take(1)).subscribe(() => {
                        this.SpinnerService.closeSpinner();
                        this.userStore.setSelectedPropertyId(undefined);
                        this.router.navigateByUrl('/properties');
                    }, (err) => {
                        this.NetworkErrorService.handleNetworkError(err, true).pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.removeProperty();
                            }
                        });
                    });
                });
            } else {
                this.SpinnerService.closeSpinner();
            }
        });
    }

    defaultCouponData() {
        //if each of the required fields for address is defined then call api to retreive coupon data
        if (this.isAddressPresent()) {
            this.propertyApi.couponGet(false, this.property.address).pipe(take(1)).subscribe((data) => {
                if (this.property.hoa) {
                    this.property.hoa.couponId = data.hoaCouponId;
                } else {
                    this.property.hoa = { couponId: data.hoaCouponId };
                }
                this.property.couponId = data.propertyCouponId;
                if (this.property.hoa.pmc) {
                    this.property.hoa.pmc.couponId = data.pmcCouponId;
                } else {
                    this.property.hoa.pmc = { couponId: data.pmcCouponId };
                }
            }, (err) => {
                this.NetworkErrorService.handleNetworkError(err, true).pipe(take(1)).subscribe((resp) => {
                    if (resp === true) {
                        this.defaultCouponData();
                    }
                });
            });
        }
    }

    preSave() {
        this.save().then(() => {}, (err) => {});
    }

    save(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.SpinnerService.showSpinner();
            if (this.isCorrectingAddress) {
                //if correcting address we will skip calling
                //defaultPropertyAddress to match the
                //coupon ID address against what is entered
                //since it will always be different
                this.saveStep2().then(() => resolve, () => reject());
            } else {
                this.defaultPropertyAddress().then(() => {
                    this.saveStep2().then(() => resolve(), () => reject());
                }, (err) => {
                    if (err === 'Invalid property') {
                        this.dialog.open(RegistrationNoPropertyDialog, {
                            closeOnNavigation: true,
                            disableClose: false,
                            data: { pmcId: this.property.hoa.pmc.couponId }
                        });
                    }
                    this.SpinnerService.closeSpinner();
                    reject();
                });
            }
        });
    }

    saveStep2(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!this.isAddressValid(this.property.address)) {
                this.dialog.open(RegistrationInvalidAddressDialog, {
                    closeOnNavigation: true,
                    disableClose: false,
                });
                this.SpinnerService.closeSpinner();
                return resolve();
            } else {
                let userId = this.userStore.getHoaHomeownerId();
                this.propertyApi.propertiesValidateGet(false,
                    this.property.hoa.pmc.couponId,
                    this.property.hoa.couponId,
                    this.property.couponId)
                    .pipe(take(1)).subscribe((resp) => {
                        if (resp.valid === true && (resp.doNotProcess === false || resp.doNotProcessCode === "L01")) {
                            this.property.nickname = this.nickname;
                            const couponData: CouponData = {
                                hoaCouponId: this.property.hoa.couponId,
                                pmcCouponId: this.property.hoa.pmc.couponId,
                                propertyCouponId: this.property.couponId,
                                propertyId: this.property.id,
                                propertyNickname: this.property.nickname,
                                address: this.property.address
                            }
                            this.homeownerApi.homeownersIdCouponUpdatePost(userId, couponData)
                                .pipe(take(1)).subscribe((resp) => {
                                    this.SpinnerService.closeSpinner();
                                    if (resp.status === 200) {
                                        this.router.navigateByUrl('/properties');
                                    }
                                    resolve();
                                },  (err) => {
                                    if (err.error.message === 'Property already registered. Cannot add the same property again' ) {
                                        this.dialog.open(OneButtonDialogComponent, {
                                            closeOnNavigation: true,
                                            disableClose: false,
                                            data: {
                                                buttonText: 'OK',
                                                header: 'Property Registration Error',
                                                message: 'The property you entered is already registered. Cannot add the property twice.'
                                            }
                                        })
                                    } else {
                                        let errorMessage = err.error.message;
                                        if (err.error.errors && err.error.errors.length > 0) {
                                            err.error.errors.forEach(msg => {
                                                errorMessage += '<br><br>' + msg;
                                            })
                                        }
                                        this.dialog.open(DialogErrorDialog, {
                                            closeOnNavigation: true,
                                            disableClose: false,
                                            data: {
                                                message: errorMessage,
                                                removeRetry: true,
                                            }
                                        });
                                    }
                                    this.SpinnerService.closeSpinner();
                                    reject();
                                });
                        } else {
                            if (resp.doNotProcessCode === "D01" || resp.doNotProcessCode === "M01") {
                                this.dialog.open(RegistrationStopHoldDialog, {
                                    closeOnNavigation: true,
                                    disableClose: false,
                                    data: { reasonCode: resp.doNotProcessCode }
                                });
                                this.SpinnerService.closeSpinner();
                            } else { //No Matching record found.
                                this.dialog.open(RegistrationNoPropertyDialog, {
                                    closeOnNavigation: true,
                                    disableClose: false,
                                    data: { pmcId: this.property.hoa.pmc.couponId }
                                });
                                this.SpinnerService.closeSpinner();
                            }
                            reject();
                        }
                    }, (err) => {
                        this.NetworkErrorService.handleNetworkError(err, true)
                            .pipe(take(1)).subscribe((resp) => {
                                if (resp === true) {
                                    this.save();
                                }
                            });
                        reject();
                    });
            }
        });
    }

    cancel() {
        this.router.navigateByUrl('/properties');
    }

    moved() {
        let dialogRef = this.dialog.open(PropertydetailsMovedDialog, {
            closeOnNavigation: true,
            disableClose: true,
        });
        dialogRef.afterClosed().pipe(take(1)).subscribe((data) => {
            if (data === 'Moved') {
                this.router.navigateByUrl('/properties/add');
            }
        });
    }

    checkIfMoved() {
        if (this.count++ === 0) {
            this.moved()
        } else {

            this.stopCorrectingAddress();
            this.defaultPropertyAddress().then(() => {}, () => {});
        }
    }

    invalidCheck(addressError) {
        if (addressError.hasOwnProperty("searching")) {
            this.isLoadingFindMyProperty = addressError["searching"];
        }
        for (let key in addressError) {
            if (addressError[key]) {
                this.invalidCheckVar = true;
                return;
            }
        }
        this.invalidCheckVar = false;
    }

    invalid() {
        return !this.isCouponDataPresent() || !this.isAddressPresent() || this.invalidCheckVar;
    }

    isCouponDataPresent() {
        return this.property && this.property.couponId &&
            this.property.couponId.toString().length > 0 &&
            this.property.hoa &&
            this.property.hoa.couponId &&
            this.property.hoa.couponId.toString().length > 0 &&
            this.property.hoa.pmc &&
            this.property.hoa.pmc.couponId &&
            this.property.hoa.pmc.couponId.toString().length > 0;
    }

    defaultPropertyAddress(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.isCouponDataPresent()) {
                this.propertyApi.propertyAddressGet(false,
                    this.property.hoa.pmc.couponId,
                    this.property.hoa.couponId,
                    this.property.couponId)
                    .pipe(take(1)).subscribe((resp) => {
                        resp.line1 = resp.addressLine1;
                        resp.line2 = resp.addressLine2;
                        if (resp.line1) {
                            this.disableInputsAddress = true;
                            if (!this.isAddressPresent()) {
                                this.setPropertyAddress(resp);
                                resolve();
                            } else {
                                if (this.property.address.line1 === resp.line1 &&
                                (this.property.address.line2 === undefined || this.property.address.line2 === resp.line2) &&
                                this.property.address.city === resp.city &&
                                this.property.address.state === resp.state &&
                                this.property.address.postalCode === resp.postalCode) {
                                // DO NOTHING = things match
                                    resolve();
                                } else {
                                    let propertyAddress = "Address Line 1: " + resp.line1 + "<br>";
                                    if (resp.line2)
                                        propertyAddress = propertyAddress + "Address Line 2: " + resp.line2 + "<br>";
                                    if (resp.unit)
                                        propertyAddress = propertyAddress + "Unit#: " + resp.unit + "<br>";
                                    propertyAddress = propertyAddress + "City: " + resp.city + "<br> State: " + resp.state + "<br>Postal Code: " + resp.postalCode;

                                    // Retrieve pmc details
                                    this.ContactApi.pmcOrganizationDetailsGET(
                                        true,
                                        this.property.hoa.pmc.couponId
                                    ).subscribe((data) => {
                                        if (data == null) {
                                            reject('No pmc found with Coupon ID: ' + this.property.hoa.pmc.couponId)
                                            return
                                        }

                                        const pmcDataName = data.name;
                                        const pmcCouponId = this.property.hoa.pmc.couponId;

                                        let dialogRef: MatDialogRef<any> = null;
                                        if (!this.isAddressValid(resp)) {
                                            dialogRef = this.dialog.open(RegistrationAltAddressNeedsCorrectionDialog, {
                                                closeOnNavigation: true,
                                                disableClose: false,
                                                data: {
                                                    propertyAddress: propertyAddress,
                                                    contactUrl: this.getPmcContactUrl(pmcCouponId),
                                                    pmcName: pmcDataName
                                                }
                                            });
                                        } else {
                                            dialogRef = this.dialog.open(RegistrationAltAddressDialog, {
                                                closeOnNavigation: true,
                                                disableClose: false,
                                                data: {
                                                    propertyAddress: propertyAddress,
                                                    contactUrl: this.getPmcContactUrl(pmcCouponId),
                                                    pmcName: pmcDataName
                                                }
                                            });
                                        }
                                        dialogRef.afterClosed().pipe(take(1)).subscribe((data) => {
                                            if (data === 'A') {
                                                this.property.nickname = this.nickname;
                                                //because we don't want type or billing summary fields or the version number to be 0;
                                                this.property.address.line1 = resp.addressLine1;
                                                this.property.address.line2 = resp.addressLine2;
                                                this.property.address.city = resp.city;
                                                this.property.address.state = resp.state;
                                                this.property.address.postalCode = resp.postalCode;
                                                this.setPropertyAddress(this.property.address);
                                                resolve();
                                            } else {
                                                reject();
                                            }
                                        });
                                    });
                                }
                            }
                        } else {
                            this.setPropertyAddressSubject.next();
                            this.disableInputsAddress = false;
                            this.propertyApi.propertiesValidateGet(false,
                                this.property.hoa.pmc.couponId,
                                this.property.hoa.couponId,
                                this.property.couponId)
                                .pipe(take(1)).subscribe((resp) => {
                                    if (resp.valid === true) {
                                        resolve();
                                    } else {
                                        reject('Invalid property');
                                    }
                                }, () => {
                                    //todo this should really be changed by the service to not be a regular 500 error,
                                    // so we can specifically catch the invalid property errors.
                                    if (!this.invalidCheckVar) {
                                        let dialogRef = this.dialog.open(RegistrationNoPropertyDialog, {
                                            closeOnNavigation: true,
                                            disableClose: false,
                                            data: { pmcId: this.property.hoa.pmc.couponId }
                                        });
                                        dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                                            reject('Invalid property');
                                        });
                                    }
                                });
                        }
                        return resp;
                    }, (err) => {
                        if (err.status === 404) {
                            reject('Invalid property');
                        } else {
                            this.NetworkErrorService.handleNetworkError(err, true).pipe(take(1)).subscribe((resp) => {
                                if (resp === true) {
                                    this.defaultPropertyAddress();
                                }
                                reject();
                            });
                        }
                    });
            } else {
                reject();
            }
        });
    }

    propertyNickname() {
        this.property.nickname = this.nickname;
        this.nicknameChanged = true;
        if (this.property.nickname === undefined) {
            this.propertyNicknameError = true;
        } else {
            this.propertyNicknameError = !this.property.nickname;
        }
    }

    defaultNickname() {
        if (!this.nicknameChanged)
            this.nickname = this.property.nickname;
    }
}
